import { getI18n } from '@/plugins/i18n'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { useProductService } from '@/services/product.service'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import { fixedDecimal } from '@/lib/utils'

export const laborPriceFields = [
  { net: 'internal_price', vat: 'internal_price_vat', netCaption: 'common.internalPrice', vatCaption: 'common.internalPriceVat' },
  { net: 'warranty_price', vat: 'warranty_price_vat', netCaption: 'common.warrantyPrice', vatCaption: 'common.warrantyPriceVat' },
]
export const laborPriceFieldNames = laborPriceFields.flatMap(({ net, vat }) => [net, vat])

const addVat = (price, rate) => price == null ? null : fixedDecimal(price * (1 + rate), 6)
const removeVat = (price, rate) => price == null ? null : fixedDecimal(price / (1 + rate), 6)

export const isProductFieldRequired = (field, productType) => {
  const { PRODUCT, LABOR, EXPENSE, TEXT } = useRegistryStore().productTypes
  const requiredProductTypes = {
    purchase_family_id: [EXPENSE],
    price_vat: [LABOR],
    unit_of_measure_ref: [PRODUCT],
    price: [LABOR],
  }
  const types = requiredProductTypes[field]
  return types ? types.includes(productType) : productType !== TEXT
}

export const useColumns = (editors) => {
  const i18n = getI18n()
  const registryStore = useRegistryStore()
  const portalStore = usePortalStore()
  const webSiteStore = useWebSiteStore()

  const getVatPercentage = (data) => webSiteStore.vatTypes.find(i => i.id === data.vat_type_ref)?.percentage || 0

  const laborPriceColumns = laborPriceFields.flatMap(({ net, vat, netCaption, vatCaption }) => [
    {
      dataField: net,
      caption: i18n.t(netCaption),
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value, currentRowData) => {
        newData[net] = value
        const actualData = { ...currentRowData, ...newData }
        newData[vat] = addVat(value, getVatPercentage(actualData))
      },
    },
    {
      dataField: vat,
      caption: i18n.t(vatCaption),
      dataType: 'number',
      visible: false,
      showInColumnChooser: false,
      setCellValue: (newData, value, currentRowData) => {
        newData[vat] = value
        const actualData = { ...currentRowData, ...newData }
        newData[net] = removeVat(value, getVatPercentage(actualData))
      },
    },
  ])

  const toggleReadOnlyValidation = (field) => ({
    type: 'custom',
    message: i18n.t('common.required'),
    reevaluate: true,
    validationCallback: (e) =>
      !isProductFieldRequired(field, e.data.product_type) || (e.value != null && e.value !== ''),
  })

  return [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '8rem',
    visibleIndex: 0,
    fixed: false
  },
  {
    dataField: 'product_type',
    caption: i18n.t('common.type'),
    cellTemplate: 'typeCellTemplate',
    alignment: 'center',
    width: '8rem',
    lookup: dataGrid.useLookup({ store: registryStore.getLocalizedProductTypes(), displayExpr: 'name' }),
    setCellValue: (newData, value, currentRowData) => {
      const clearFields = fields => fields.forEach(field => { newData[field] = null })

      newData.product_type = value
      if (value !== registryStore.productTypes.LABOR) {
        clearFields([...laborPriceFieldNames, 'technical_qualification_id'])
      }
      if (value !== registryStore.productTypes.EXPENSE) {
        newData.expense_type_id = null
      }
      if (value !== registryStore.productTypes.TEXT) {
        newData.currency_ref = registryStore.companyData?.default_currency_ref
        newData.vat_type_ref = registryStore.companyData?.default_vat_type_ref
        if (newData.price == null) {
          newData.price = 0
        }
        if (newData.price_vat == null) {
          newData.price_vat = 0
        }
      }

      switch (value) {
        case registryStore.productTypes.TEXT:
          clearFields([
            'product_availability', 'currency_ref', 'price', 'price_vat', 'vat_type_ref',
            'cost', 'cost_vat', 'discount_category_ref', 'price_date', 'unit_of_measure_ref',
            'weight_kg', 'package_quantity', 'minimum_quantity',
          ])
          break
        case registryStore.productTypes.LABOR:
          newData.unit_of_measure_ref = webSiteStore.unitsOfMeasure.find(el => el.code === webSiteStore.unitOfMeasureOfTimeEnum.UL)?.id
          break
        case registryStore.productTypes.PRODUCT:
          clearFields(['product_availability', 'cost', 'cost_vat'])
          newData.unit_of_measure_ref = registryStore.companyData?.default_unit_of_measure_ref
          break
        case registryStore.productTypes.SERVICE:
        case registryStore.productTypes.EXPENSE:
          clearFields([
            'product_availability', 'supplier_order_group_ref', 'weight_kg',
            'package_quantity', 'minimum_quantity',
          ])
          newData.unit_of_measure_ref = registryStore.companyData?.default_unit_of_measure_ref
          break
      }
    },
    validationRules: [{ type: 'required' }],
  },
  dataGridColumn.useSupplierBrandColumn({
    sortOrder: 'asc',
    width: '8rem',
    setCellValue: (newData, value, currentRowData) => {
      newData.supplier_brand_ref = value
    },
    validationRules: [{ type: 'required' }]
  }),
  {
    dataField: 'code',
    caption: i18n.t('common.code'),
    filterOperations: ['contains'],
    sortOrder: 'asc',
    width: '10rem',
    validationRules: [{ type: 'required' }]
  },
  {
    dataField: 'name',
    caption: i18n.t('common.description'),
    filterOperations: ['contains'],
    allowSorting: false,
    validationRules: [{ type: 'required' }]
  },
  dataGridColumn.useProductFamilyColumn({
    allowSorting: false,
  }),
  {
    caption: i18n.t('common.expenseType'),
    dataField: 'expense_type_id',
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedExpenseTypes() }),
    visible: false,
    showInColumnChooser: false,
  },
  {
    caption: i18n.t('common.technicalQualification'),
    dataField: 'technical_qualification_id',
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedTechnicalQualifications() }),
    visible: false,
    showInColumnChooser: false,
  },
  {
    caption: i18n.t('common.purchaseFamily'),
    dataField: 'purchase_family_id',
    alignment: 'center',
    width: '10rem',
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedPurchaseFamilies() }),
    allowSorting: false,
    setCellValue: (newData, value, currentRowData) => {
      newData.purchase_family_id = value
      if (currentRowData.product_type === registryStore.productTypes.EXPENSE) {
        if (portalStore.getPurchaseFamilyById(value)?.isPercentageValue) {
          newData.cost = newData.cost_vat = newData.price = newData.price_vat = newData.price_date = null
        } else {
          newData.value_percent = null
        }
      }
    },
    validationRules: [toggleReadOnlyValidation('purchase_family_id')]
  },
  dataGridColumn.useProductAvailabilityColumn({caption: i18n.t('common.productAvailabilityAlt'), allowSorting: false}),
  dataGridColumn.useCurrencyColumn({
    allowSorting: false, showInColumnChooser: false,
    validationRules: [toggleReadOnlyValidation('currency_ref')],
  }),
  dataGridColumn.usePriceColumn({
    caption: i18n.t('common.priceListProduct'),
    allowSorting: false,
    allowFiltering: false,
    setCellValue: (newData, value, currentRowData) => {
      const { setPriceValue } = useProductService()
      newData.price_date = new Date().toISOString().split('T')[0]
      newData.previous_price = value
      setPriceValue(newData, value, currentRowData)
    },
    validationRules: [toggleReadOnlyValidation('price')]
  }),
  dataGridColumn.useCostColumn({
    caption: i18n.t('common.cost'),
    allowSorting: false,
    allowFiltering: false,
    setCellValue: (newData, value, currentRowData) => {
      const { setCostValue } = useProductService()
      setCostValue(newData, value, currentRowData)
    },
    validationRules: []
  }),
  dataGridColumn.useValuePercentColumn({ allowSorting: false }),
  dataGridColumn.useVatTypeColumn({
    allowSorting: false,
    setCellValue: (newData, value, currentRowData) => {
      const { setVatTypeValue } = useProductService()
      setVatTypeValue(newData, value, currentRowData)

      const actualData = { ...currentRowData, ...newData }
      if (actualData.product_type === registryStore.productTypes.LABOR) {
        const vatRate = getVatPercentage(actualData)
        laborPriceFields.forEach(({ net, vat }) => {
          newData[vat] = addVat(actualData[net], vatRate)
        })
      }
    },
    validationRules: [toggleReadOnlyValidation('vat_type_ref')],
  }),
  dataGridColumn.useUnitOfMeasureColumn({
    allowSorting: false,
    setCellValue: (newData, value, currentRowData) => {
      newData.unit_of_measure_ref = value
    },
    validationRules: [toggleReadOnlyValidation('unit_of_measure_ref')],
    conversionsFilter: () => editors['product_type']?.option('value') !== registryStore.productTypes.LABOR,
  }),
  dataGridColumn.useDiscountCategoryColumn({ allowSorting: false }),
  { dataField: 'price_date', caption: i18n.t('common.priceDate'), width: '10rem', dataType: 'date', allowSorting: false, allowFiltering: false },
  dataGridColumn.useSupplierOrderGroupColumn({ visible: false, showInColumnChooser: false, allowSorting: false }),
  dataGridColumn.usePriceVatColumn({ caption: i18n.t('common.priceListProductVat'), allowSorting: false, showInColumnChooser: false, validationRules: [toggleReadOnlyValidation('price_vat')] }),
  dataGridColumn.useCostVatColumn({ allowSorting: false, showInColumnChooser: false }),
  ...laborPriceColumns,
  { dataField: 'weight_kg', caption: i18n.t('common.weight'), width: '10rem', dataType: 'number', visible: false, showInColumnChooser: false },
  { dataField: 'package_quantity', caption: i18n.t('common.packageQuantity'), width: '10rem', dataType: 'number', visible: false, showInColumnChooser: false },
  { dataField: 'minimum_quantity', caption: i18n.t('common.minimumQuantity'), width: '10rem', dataType: 'number', visible: false, showInColumnChooser: false },
  {
    dataField: 'product_analytic_group_ref',
    caption: i18n.t('common.productAnalyticGroup'),
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedProductAnalyticGroups(), displayExpr: 'name' }),
    visible: false, showInColumnChooser: false
  },
  {
    dataField: 'product_typology_ref',
    caption: i18n.t('common.productTypology'),
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedProductTypologies(), displayExpr: 'name' }),
    visible: false, showInColumnChooser: false
  },
  {
    dataField: 'product_manufacturer_family_ref',
    caption: i18n.t('common.productManufacturerFamily'),
    lookup: dataGrid.useLookup({ store: portalStore.getLocalizedProductManufacturerFamilies(), displayExpr: 'name' }),
    visible: false, showInColumnChooser: false
  },
  {
    dataField: 'show_in_schedule',
    caption: i18n.t('common.viewInSchedule'),
    dataType: 'boolean',
    visible: false,
    showInColumnChooser: false,
  },
  { dataField: 'product_accounting_intrastat_code',
    caption: i18n.t('common.intrastatCode'),
    visible: false,
    showInColumnChooser: true,
    allowSorting: false,
    allowFiltering: false,
  },
  {
    dataField: 'product_accounting_trade_account_purchase_ref',
    caption: i18n.t('common.tradeAccountPurchase'),
    visible: false,
    showInColumnChooser: true,
    allowSorting: false,
    allowFiltering: false,
  },
  {
    dataField: 'product_accounting_trade_account_sale_ref',
    caption: i18n.t('common.tradeAccountSale'),
    visible: false,
    showInColumnChooser: true,
    allowSorting: false,
    allowFiltering: false,
  },
  ]
}
